import {
  endOfMonth,
  startOfMonth,
  startOfDay,
  addSeconds,
  formatDistance,
  subSeconds,
  isValid as isValidFns
} from 'date-fns';
import { utcToZonedTime, format } from 'date-fns-tz';

export type UiDate = string | 'now';
export type DateType = Date | number | UiDate;

const TIME_FORMAT = 'HH:mm:ss';
const YEAR_FORMAT = 'yyyy';
const MONTH_FORMAT = 'yyyy-MM';
const DAY_FORMAT = 'yyyy-MM-dd';
export const DATE_TIME_FORMAT = "yyyy-MM-dd'T'HH:mm";
export const COMPLETE_DATE_TIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ss";
export const API_DATE_FORMAT = "yyyy-MM-dd'T'HH:mm:ssXXX";
export const TABLE_DATE_TIME_FORMAT = 'yyyy-MM-dd, HH:mm:ss';
export const TABLE_TEXT_DATE_TIME_FORMAT = 'MMM dd yyyy, HH:mm:ss';

export const formatDate = (
  date: DateType,
  dateFormat: string,
  timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone
): string => format(utcToZonedTime(new Date(date), timeZone), dateFormat, { timeZone });

type FormatDateToApiFormat = ({
  date,
  timeZone,
  type
}: {
  date: DateType;
  timeZone?: string;
  type?: 'inclusive' | 'exclusive';
}) => string;

type FormatDateToClientFormat = ({
  date,
  dateFormat,
  timeZone,
  type
}: {
  date: DateType;
  dateFormat?: string;
  timeZone?: string;
  type?: 'inclusive' | 'exclusive';
}) => string;

/**
 * @param date
 * @param timeZone
 * @param type: 'inclusive' or 'exclusive' - inclusive means that date and time will be included into results,
 * exclusive means that date and time won't be include
 */
export const formatDateToApiFormat: FormatDateToApiFormat = ({
  date,
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  type = 'inclusive'
}): string => {
  if (date === 'now') {
    date = new Date();
  }
  if (type === 'exclusive') {
    return format(addSeconds(new Date(date), 1), API_DATE_FORMAT, { timeZone });
  }
  return format(new Date(date), API_DATE_FORMAT, { timeZone });
};

/**
 * @param date
 * @param dateFormat
 * @param timeZone
 * @param type: 'inclusive' or 'exclusive' - inclusive means that date and time from server includes it into result,
 * exclusive means that date and time won't be include
 */
export const formatDateToClientFormat: FormatDateToClientFormat = ({
  date,
  dateFormat = TABLE_DATE_TIME_FORMAT,
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  type = 'inclusive'
}): string => {
  if (date === 'now') {
    date = new Date();
  }
  if (type === 'exclusive') {
    return format(subSeconds(new Date(date), 1), dateFormat, { timeZone });
  }
  return format(new Date(date), dateFormat, { timeZone });
};

export const getCurrentDay = (date: DateType = new Date()): string => formatDate(new Date(date), DAY_FORMAT);
export const getCurrentTime = (date: DateType = new Date()): string => formatDate(new Date(date), TIME_FORMAT);
export const getCurrentMonth = (date: DateType = new Date()): string => formatDate(new Date(date), MONTH_FORMAT);
export const getCurrentYear = (date: DateType = new Date()): string => formatDate(new Date(date), YEAR_FORMAT);

export const getBeginningOfMonth = (date: DateType = new Date()): string =>
  formatDate(startOfMonth(new Date(date)), COMPLETE_DATE_TIME_FORMAT);

export const getEndOfMonth = (date: DateType = new Date()): string =>
  formatDate(endOfMonth(new Date(date)), COMPLETE_DATE_TIME_FORMAT);

export const getBeginningOfDay = (date: DateType = new Date()): string =>
  formatDate(startOfDay(new Date(date)), COMPLETE_DATE_TIME_FORMAT);

export const getCurrentMoment = (date: DateType = new Date()): string =>
  formatDate(new Date(date), COMPLETE_DATE_TIME_FORMAT);

export const convertSecondsToTimeFormat = (seconds: number | null): string => {
  if (seconds === null) {
    return '-';
  }
  const time = addSeconds(new Date(0), seconds);

  if (seconds < 60) {
    return formatDate(time, "s's'");
  }

  if (seconds % 60 === 0) {
    return formatDate(time, "m'm'");
  }

  return formatDate(time, "m'm' s's'");
};

export const humanizeDistanceBetweenDates = (date: Date | number | string, baseDate = new Date()) =>
  formatDistance(new Date(date), baseDate, { addSuffix: true });

export type DefaultTime = [string, string, string, string];

export const defaultFromTime: DefaultTime = ['00', '00', '00', '000'];
export const defaultToTime: DefaultTime = ['23', '59', '59', '999'];

export const dateWithTimeSet = (date: Date, defaultTime: DefaultTime) => {
  const [hours, minutes, seconds, milliseconds] = defaultTime;
  date = new Date(date);
  date.setHours(parseInt(hours, 10));
  date.setMinutes(parseInt(minutes, 10));
  date.setSeconds(parseInt(seconds, 10));
  date.setMilliseconds(parseInt(milliseconds, 10));
  return date;
};

export const getTimeRange = ({ from, to, timeZone }: { from: DateType; to: DateType; timeZone?: string }) => ({
  from: from === null ? null : formatDateToApiFormat({ date: from, type: 'inclusive', timeZone }),
  to: to === null ? null : formatDateToApiFormat({ date: to, type: 'exclusive', timeZone })
});

export const isValid = (date: DateType) => isValidFns(new Date(date));

export const handleEndDateFilter = (endDate: UiDate) => (endDate === 'now' ? getCurrentMoment() : endDate);
